/**
 * BillingReviewModal — invoice one project from Cortex (Matt, 2026-11-04; docs/BILLING_INVOICE_STUDY.md
 * "The per-project review"). Core's batch, one project at a time: the phases with the calculated fee,
 * every entry editable, percent complete with a floor at what is billed, check the PDF, Finalize, then Send (the Billing Run email) and next.
 *
 * Opens on `open-billing-review` { code, name?, through? }. Billing admins only (the server checks).
 * Fixed-fee write-ups/downs are calculated, never typed (Matt: billing ahead and working past 100%
 * needs no step); hourly phases take a percent per entry. Client hours and memo save to Core at once.
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { getReview, adjustEntry, editEntry, setPhasePercent, sendInvoice, getLedgerPdf, computePhase } from '../services/billingInvoices.js'
import { billingThroughFor, getCortexDraftPdf } from '../services/billing.js'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import { ctx } from '../outline/ctx.js'

const SANS = '"DM Sans", sans-serif'
const MONO = '"DM Mono", monospace'
const money = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const hrs = n => (Math.round((Number(n) || 0) * 100) / 100).toString()
const pctTxt = n => (n == null ? '' : (n > 0 ? '+' : '') + (Math.round(n * 10) / 10) + '%')
const CHOICES = [['bill', 'Bill'], ['exclude', 'Next invoice'], ['write_off', "Don't bill"]]

function actor() {
  try { const f = ctx.currentMember && ctx.currentMember.fields; return (f && (f['Member Name'] || f['Name'])) || '' } catch (e) { return '' }
}

async function openBlob(getBlob) {
  const w = window.open('', '_blank')
  try {
    const b = await getBlob()
    const url = URL.createObjectURL(b)
    if (w) w.location.href = url; else window.open(url, '_blank')
  } catch (e) {
    if (w) w.close()
    alertDialog({ title: 'Could not open the PDF', message: e.message })
  }
}

export default function BillingReviewModal() {
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [through, setThrough] = useState('')
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState('')
  const [openPhase, setOpenPhase] = useState({})
  const [showQuiet, setShowQuiet] = useState(false)
  const [sel, setSel] = useState({})          // entry id -> true
  const [bulkWud, setBulkWud] = useState('')
  const [result, setResult] = useState(null)

  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      setCode(String(d.code || '')); setThrough(d.through || billingThroughFor())
      setData(null); setErr(''); setResult(null); setSel({}); setOpenPhase({}); setOpen(true)
    }
    window.addEventListener('open-billing-review', onOpen)
    return () => window.removeEventListener('open-billing-review', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape' && !busy) setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [open, busy])

  const load = useCallback(async () => {
    if (!code || !through) return
    setErr('')
    try { setData(await getReview(code, through)) } catch (e) { setErr(e.message) }
  }, [code, through])
  useEffect(() => { if (open) load() }, [open, load])

  const sent = !!(data && data.invoice && data.invoice.status === 'sent')
  const lines = useMemo(() => {
    if (!data) return {}
    const o = {}
    for (const p of data.phases) o[p.id] = computePhase(p, data.time, data.expenses)
    return o
  }, [data])
  const totals = useMemo(() => {
    const v = Object.values(lines)
    return { service: v.reduce((s, x) => s + x.service, 0), expenses: v.reduce((s, x) => s + x.expenses, 0), total: v.reduce((s, x) => s + x.total, 0) }
  }, [lines])

  // ── local edits, saved at once ──
  function patchEntry(kind, id, patch) {
    setData(d => ({ ...d, [kind === 'time' ? 'time' : 'expenses']: d[kind === 'time' ? 'time' : 'expenses'].map(x => x.id === id ? { ...x, ...patch } : x) }))
  }
  async function saveChoice(kind, x, choice, wud) {
    const prev = { choice: x.choice, wud: x.wud }
    patchEntry(kind, x.id, { choice, wud: wud == null ? 0 : wud })
    try { await adjustEntry({ through, kind, coreEntryId: x.id, choice, wud: wud == null ? null : wud, actorName: actor() }) }
    catch (e) { patchEntry(kind, x.id, prev); alertDialog({ title: 'Not saved', message: e.message }) }
  }
  async function saveCoreField(kind, x, field, value) {
    const prev = { [field]: x[field] }
    patchEntry(kind, x.id, { [field]: value })
    try { await editEntry({ kind, coreEntryId: x.id, [field]: value, actorName: actor() }) }
    catch (e) { patchEntry(kind, x.id, prev); alertDialog({ title: 'Core did not take the change', message: e.message }) }
  }
  async function savePercent(p, value) {
    const v = value === '' ? null : Number(value)
    if (v == null || v === p.percent) return
    const prev = p.percent
    setData(d => ({ ...d, phases: d.phases.map(q => q.id === p.id ? { ...q, percent: v } : q) }))
    try { await setPhasePercent({ through, code: data.project.code, phaseCoreId: p.id, percent: v, actorName: actor() }) }
    catch (e) {
      setData(d => ({ ...d, phases: d.phases.map(q => q.id === p.id ? { ...q, percent: prev } : q) }))
      alertDialog({ title: 'Percent not saved', message: e.message })
    }
  }
  async function bulk(kind, list, choice) {
    const wud = choice === 'bill' && bulkWud !== '' ? Number(bulkWud) : null
    setBusy('Saving…')
    for (const x of list) await saveChoice(kind, x, choice, wud)
    setBusy(''); setSel({}); setBulkWud('')
  }

  async function doSend() {
    const nTime = data.time.filter(x => x.choice !== 'exclude').length
    const nExp = data.expenses.filter(x => x.choice !== 'exclude').length
    const ok = await confirmDialog({
      title: 'Finalize invoice ' + data.nextNumber + '?',
      message: money(totals.total) + ' for ' + data.project.code + ' ' + data.project.name + ', through ' + through + '.\n\n' +
        'Cortex records it, creates invoice ' + data.nextNumber + ' in Core for the books, and marks ' + nTime + ' time ' +
        (nTime === 1 ? 'entry' : 'entries') + ' and ' + nExp + ' ' + (nExp === 1 ? 'expense' : 'expenses') + ' billed in Core. ' +
        'Entries left for next time stay unbilled. The client is not emailed yet: use Send in the Billing Run after this.',
      confirmLabel: 'Finalize ' + data.nextNumber,
    })
    if (!ok) return
    setBusy('Finalizing… (recording it, creating it in Core, marking the entries billed)'); setErr('')
    try {
      const r = await sendInvoice({ through, code: data.project.code, actorName: actor() })
      setResult(r)
      await load()
    } catch (e) { setErr(e.message) }
    setBusy('')
  }


  if (!open) return null
  const phases = data ? data.phases : []
  const hasWork = p => data.time.some(x => x.phaseId === p.id) || data.expenses.some(x => x.phaseId === p.id) || Math.abs((lines[p.id] || {}).total || 0) > 0.005
  const active = data ? phases.filter(hasWork) : []
  const quiet = data ? phases.filter(p => !hasWork(p)) : []
  const number = sent ? data.invoice.invoice_number : data && data.nextNumber

  return createPortal(
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget && !busy) setOpen(false) }}>
      <style>{CSS}</style>
      <div style={S.modal} className="aj-br">
        <div style={S.bar}>
          <span style={S.title}>{sent ? 'Invoice' : 'Review invoice'}</span>
          <button style={S.close} onClick={() => !busy && setOpen(false)} aria-label="Close">×</button>
        </div>

        <div style={S.scroll}>
          {!data && !err && (
            <div style={S.loading}>
              <div style={S.spinner} className="aj-br-spin" />
              <div style={S.loadTitle}>Reading {code} from Core</div>
              <div style={S.loadSub}>Unbilled hours, expenses and what each phase has billed.</div>
            </div>
          )}

          {data && (
            <>
              {/* ── the summary ── */}
              <div style={S.hero}>
                <div style={{ minWidth: 0 }}>
                  <div style={S.eyebrow}>{sent ? 'Invoice ' + number + ' · finalized ' + String(data.invoice.sent_at || '').slice(0, 10) : 'Invoice ' + number + ' · draft'}</div>
                  <div style={S.project}>{data.project.name}</div>
                  <div style={S.period}>{data.project.code} · work through {longDate(through)}</div>
                </div>
                <div style={S.heroRight}>
                  <div style={S.total}>{money(sent ? data.invoice.total : totals.total)}</div>
                  <div style={S.split}>
                    Fees {money(sent ? data.invoice.service_total : totals.service)} · Expenses {money(sent ? data.invoice.expense_total : totals.expenses)}
                  </div>
                  <div style={S.actions}>
                    {sent
                      ? <button className="aj-br-btn" style={S.btn} onClick={() => openBlob(() => getLedgerPdf(data.invoice.id))}>Open invoice PDF</button>
                      : <>
                          <button className="aj-br-ghost" style={S.btnGhost} disabled={!!busy} onClick={() => openBlob(() => getCortexDraftPdf(data.project.code, through))}>Preview PDF</button>
                          <button className="aj-br-btn" style={S.btn} disabled={!!busy || !(totals.total > 0.005)} onClick={doSend}>Finalize invoice</button>
                        </>}
                  </div>
                </div>
              </div>

              {result && (
                <div style={S.done}>
                  <strong>Invoice {result.invoiceNumber} is finalized</strong>{result.posted ? ' and posted in Core.' : '. Core kept it as a draft, so post it in Core.'}{' '}
                  Use Send in the Billing Run to email it to the client.
                  {(result.notMarked.time.length + result.notMarked.expenses.length) > 0 &&
                    ' ' + (result.notMarked.time.length + result.notMarked.expenses.length) + ' entries could not be marked billed in Core; mark them there.'}
                  {!result.pdfOk && ' The PDF was not stored; tell Claude.'}
                </div>
              )}
              {busy && <div style={S.busy}>{busy}</div>}
              {err && <div style={S.err}>{err}</div>}

              {/* ── what this invoice bills ── */}
              <div style={S.section}>On this invoice</div>
              {active.length === 0 && <div style={S.emptyCard}>Nothing to bill through {longDate(through)}.</div>}
              {active.map(p => <PhaseCard key={p.id} p={p} l={lines[p.id]} data={data} sent={sent}
                open={openPhase[p.id] !== false} onToggle={() => setOpenPhase(o => ({ ...o, [p.id]: o[p.id] === false }))}
                sel={sel} setSel={setSel} bulkWud={bulkWud} setBulkWud={setBulkWud} bulk={bulk}
                savePercent={savePercent} saveChoice={saveChoice} saveCoreField={saveCoreField} />)}

              {/* ── the rest of the project ── */}
              {quiet.length > 0 && (
                <>
                  <button className="aj-br-disclose" style={S.disclose} onClick={() => setShowQuiet(v => !v)}>
                    <span style={{ ...S.chev, transform: showQuiet ? 'rotate(90deg)' : 'none' }}>›</span>
                    Other phases <span style={S.discloseSub}>{quiet.length} with nothing to bill</span>
                  </button>
                  {showQuiet && (
                    <div style={S.quietCard}>
                      <div style={{ ...S.qRow, ...S.qHead }}>
                        <span>Phase</span><span>Type</span><span style={S.r}>Complete</span><span style={S.r}>Billed</span><span style={S.r}>Contract</span>
                      </div>
                      {quiet.map(p => <QuietRow key={p.id} p={p} sent={sent} savePercent={savePercent} />)}
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {!data && err && <div style={{ ...S.err, margin: 24 }}>{err}</div>}
        </div>
      </div>
    </div>,
    document.body
  )
}

function longDate(d) {
  try { return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) } catch (e) { return d }
}

function PercentField({ p, sent, savePercent }) {
  const [v, setV] = useState(p.percent == null ? '' : String(p.percent))
  useEffect(() => { setV(p.percent == null ? '' : String(p.percent)) }, [p.percent])
  return (
    <span style={S.pctWrap} onClick={e => e.stopPropagation()}>
      <input className="aj-br-edit" style={S.pctIn} value={v} disabled={sent} onChange={e => setV(e.target.value)}
        onBlur={() => savePercent(p, v)} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }} />
      <span style={S.pctSign}>%</span>
    </span>
  )
}

function PhaseCard({ p, l, data, sent, open, onToggle, sel, setSel, bulkWud, setBulkWud, bulk, savePercent, saveChoice, saveCoreField }) {
  const time = data.time.filter(x => x.phaseId === p.id)
  const exp = data.expenses.filter(x => x.phaseId === p.id)
  const fixed = p.kind === 'fixed'
  const c = p.contract || 0
  const before = fixed && c > 0 ? Math.min(100, p.billedBefore / c * 100) : 0
  const now = fixed && c > 0 ? Math.max(0, Math.min(100 - before, l.service / c * 100)) : 0
  const pickedTime = time.filter(x => sel[x.id]), pickedExp = exp.filter(x => sel[x.id])
  const allTime = time.length > 0 && pickedTime.length === time.length
  const name = String(p.name || '').replace(/ Phase$/, '')
  return (
    <div style={S.card}>
      <div style={S.cardHead} onClick={onToggle}>
        <div style={{ minWidth: 0 }}>
          <div style={S.phaseName}>{name}</div>
          <div style={S.phaseMeta}>{p.code} · {fixed ? 'Fixed fee' : 'Hourly'}{time.length ? ' · ' + hrs(l.hours) + ' h' : ''}{exp.length ? ' · ' + exp.length + (exp.length === 1 ? ' expense' : ' expenses') : ''}</div>
        </div>
        <div style={S.cardAmt}>
          <div style={S.amt}>{money(l.total)}</div>
          {l.expenses > 0.005 && <div style={S.amtSub}>Fee {money(l.service)} · Expenses {money(l.expenses)}</div>}
        </div>
      </div>

      {fixed && (
        <div style={S.feeBlock}>
          <div style={S.barTrack}>
            <div style={{ ...S.barBefore, width: before + '%' }} />
            <div style={{ ...S.barNow, left: before + '%', width: now + '%', background: l.over ? '#b3261e' : '#2e7d5b' }} />
          </div>
          <div style={S.feeRow}>
            <span style={S.feeLbl}>Complete <PercentField p={p} sent={sent} savePercent={savePercent} /></span>
            <span style={S.feeFacts}>
              <span><span style={S.dotBefore} />Billed before {money(p.billedBefore)}</span>
              <span><span style={{ ...S.dotNow, background: l.over ? '#b3261e' : '#2e7d5b' }} />This invoice {money(l.service)}</span>
              <span style={l.over ? S.overTxt : null}>{money(l.billedAfter)} of {money(c)}</span>
            </span>
          </div>
          {l.wud != null && time.length > 0 && (
            <div style={S.hint}>The {hrs(l.hours)} hours ({money(l.standard)} at rates) are written {l.wud < 0 ? 'down' : 'up'} {Math.abs(l.wud)}% each to equal the fee.</div>
          )}
          {l.notes.length > 0 && <div style={S.hint}>{l.notes.join(' · ')}</div>}
        </div>
      )}

      <button className="aj-br-disclose" style={S.subDisclose} onClick={onToggle}>
        <span style={{ ...S.chev, transform: open ? 'rotate(90deg)' : 'none' }}>›</span>
        {open ? 'Hide entries' : 'Show entries'}
      </button>

      {open && time.length > 0 && (
        <div style={S.tableWrap}>
          <div style={S.tableTitle}>
            Time
            {!sent && pickedTime.length > 0 && (
              <span style={S.bulk}>
                {pickedTime.length} selected
                {CHOICES.map(([v, t]) => <button key={v} className="aj-br-ghost" style={S.bulkBtn} onClick={() => bulk('time', pickedTime, v)}>{t}</button>)}
                {!fixed && <>
                  <input className="aj-br-edit" style={S.bulkIn} placeholder="±%" value={bulkWud} onChange={e => setBulkWud(e.target.value)} />
                  <button className="aj-br-ghost" style={S.bulkBtn} onClick={() => bulk('time', pickedTime, 'bill')}>Apply</button>
                </>}
              </span>
            )}
          </div>
          <div style={{ ...S.tRow, ...S.tHead }}>
            {!sent ? <input type="checkbox" checked={allTime} onChange={() => setSel(s => { const o = { ...s }; time.forEach(x => { o[x.id] = !allTime }); return o })} /> : <span />}
            <span>Date</span><span>Person</span><span style={S.r}>Worked</span><span style={S.r}>Billed</span><span style={S.r}>Rate</span>
            <span style={S.r}>{fixed ? 'Adjusted' : 'Adjust'}</span><span style={S.r}>Amount</span><span>Status</span>
          </div>
          {time.map(x => <TimeRow key={x.id} x={x} p={p} l={l} sent={sent} checked={!!sel[x.id]}
            onCheck={() => setSel(s => ({ ...s, [x.id]: !s[x.id] }))} saveChoice={saveChoice} saveCoreField={saveCoreField} />)}
        </div>
      )}

      {open && exp.length > 0 && (
        <div style={S.tableWrap}>
          <div style={S.tableTitle}>
            Expenses
            {!sent && pickedExp.length > 0 && (
              <span style={S.bulk}>
                {pickedExp.length} selected
                {CHOICES.map(([v, t]) => <button key={v} className="aj-br-ghost" style={S.bulkBtn} onClick={() => bulk('expense', pickedExp, v)}>{t}</button>)}
              </span>
            )}
          </div>
          <div style={{ ...S.eRow, ...S.tHead }}>
            <span /><span>Date</span><span>Expense</span><span style={S.r}>Amount</span><span>Status</span>
          </div>
          {exp.map(x => <ExpRow key={x.id} x={x} sent={sent} checked={!!sel[x.id]}
            onCheck={() => setSel(s => ({ ...s, [x.id]: !s[x.id] }))} saveChoice={saveChoice} saveCoreField={saveCoreField} />)}
        </div>
      )}
    </div>
  )
}

function Memo({ value, sent, onSave }) {
  const [v, setV] = useState(value)
  useEffect(() => { setV(value) }, [value])
  return <textarea className="aj-br-memo" style={S.memo} rows={1} value={v} disabled={sent} placeholder="Add a description"
    onChange={e => setV(e.target.value)} onBlur={() => { if (v !== value) onSave(v) }} />
}

function StatusSelect({ value, sent, onChange }) {
  return (
    <select className="aj-br-select" style={{ ...S.status, ...(value === 'bill' ? {} : value === 'exclude' ? S.statusNext : S.statusOff) }}
      value={value} disabled={sent} onChange={e => onChange(e.target.value)}>
      {CHOICES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
    </select>
  )
}

function TimeRow({ x, p, l, sent, checked, onCheck, saveChoice, saveCoreField }) {
  const [ch, setCh] = useState(x.clientHours == null ? '' : String(x.clientHours))
  const [wud, setWud] = useState(x.wud ? String(x.wud) : '')
  useEffect(() => { setCh(x.clientHours == null ? '' : String(x.clientHours)) }, [x.clientHours])
  const h = x.clientHours != null ? x.clientHours : x.actualHours
  const fixed = p.kind === 'fixed'
  const pct = fixed ? (l.wud || 0) : (Number(x.wud) || 0)
  const amt = x.choice === 'write_off' ? 0 : h * x.billRate * (1 + pct / 100)
  return (
    <div style={{ ...S.tItem, ...(x.choice !== 'bill' ? S.dim : {}) }}>
      <div style={S.tRow}>
        {!sent ? <input type="checkbox" checked={checked} onChange={onCheck} /> : <span />}
        <span style={S.mono}>{shortDate(x.date)}</span>
        <span style={S.who}>{x.resource}<span style={S.act}>{x.activity.replace(/^AS-\s*/, '')}</span></span>
        <span style={{ ...S.mono, ...S.r, color: 'var(--aj-text-muted)' }}>{hrs(x.actualHours)}</span>
        <span style={S.r}><input className="aj-br-edit" style={S.numIn} placeholder={hrs(x.actualHours)} value={ch} disabled={sent}
          onChange={e => setCh(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
          onBlur={() => { const v = ch === '' ? null : Number(ch); if (v !== x.clientHours) saveCoreField('time', x, 'clientHours', v) }} /></span>
        <span style={{ ...S.mono, ...S.r }}>{money(x.billRate)}</span>
        <span style={S.r}>{fixed
          ? <span style={{ ...S.mono, color: 'var(--aj-text-muted)' }}>{x.choice === 'write_off' ? '−100%' : pctTxt(l.wud) || '—'}</span>
          : <input className="aj-br-edit" style={S.numIn} placeholder="0%" value={wud} disabled={sent} onChange={e => setWud(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
              onBlur={() => { const v = wud === '' ? null : Number(wud); if ((v || 0) !== (Number(x.wud) || 0)) saveChoice('time', x, x.choice, v) }} />}</span>
        <span style={{ ...S.mono, ...S.r, color: 'var(--aj-text)' }}>{x.choice === 'exclude' ? '—' : money(amt)}</span>
        <StatusSelect value={x.choice} sent={sent} onChange={v => saveChoice('time', x, v, fixed ? null : (Number(x.wud) || null))} />
      </div>
      <div style={S.memoRow}>
        <Memo value={x.memo} sent={sent} onSave={v => saveCoreField('time', x, 'memo', v)} />
        {x.billStatus === 1 && <span style={S.badge}>On a Core batch draft</span>}
      </div>
    </div>
  )
}

function ExpRow({ x, sent, checked, onCheck, saveChoice, saveCoreField }) {
  return (
    <div style={{ ...S.tItem, ...(x.choice !== 'bill' ? S.dim : {}) }}>
      <div style={S.eRow}>
        {!sent ? <input type="checkbox" checked={checked} onChange={onCheck} /> : <span />}
        <span style={S.mono}>{shortDate(x.date)}</span>
        <span style={S.who}>{x.description.replace(/^EX-\s*/, '').replace(/:$/, '')}{x.vendor && <span style={S.act}>{x.vendor}</span>}</span>
        <span style={{ ...S.mono, ...S.r, color: 'var(--aj-text)' }}>{x.choice === 'exclude' ? '—' : money(x.choice === 'write_off' ? 0 : x.amount)}</span>
        <StatusSelect value={x.choice} sent={sent} onChange={v => saveChoice('expense', x, v, null)} />
      </div>
      <div style={S.memoRow}>
        <Memo value={x.memo} sent={sent} onSave={v => saveCoreField('expense', x, 'memo', v)} />
        {x.billStatus === 1 && <span style={S.badge}>On a Core batch draft</span>}
      </div>
    </div>
  )
}

function QuietRow({ p, sent, savePercent }) {
  const fixed = p.kind === 'fixed'
  return (
    <div style={S.qRow}>
      <span style={S.qName}>{String(p.name || '').replace(/ Phase$/, '')} <span style={S.qCode}>{p.code}</span></span>
      <span style={S.qType}>{fixed ? 'Fixed fee' : 'Hourly'}</span>
      <span style={S.r}>{fixed ? <PercentField p={p} sent={sent} savePercent={savePercent} /> : <span style={S.qDash}>—</span>}</span>
      <span style={{ ...S.mono, ...S.r }}>{money(p.billedBefore)}</span>
      <span style={{ ...S.mono, ...S.r, color: 'var(--aj-text-muted)' }}>{fixed && p.contract ? money(p.contract) : '—'}</span>
    </div>
  )
}

function shortDate(d) {
  try { return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) } catch (e) { return d }
}

const CSS = `
.aj-br-edit { border: 0.5px solid transparent !important; background: transparent !important; transition: border-color .12s, background .12s; }
.aj-br-edit:hover:not(:disabled) { border-color: var(--aj-border-mid) !important; }
.aj-br-edit:focus { outline: none; border-color: #07272D !important; background: var(--aj-bg-page) !important; }
.aj-br-memo { border: 0.5px solid transparent; background: transparent; transition: border-color .12s, background .12s; }
.aj-br-memo:hover:not(:disabled) { border-color: var(--aj-border); }
.aj-br-memo:focus { outline: none; border-color: #07272D; background: var(--aj-bg-page); }
.aj-br-select { -webkit-appearance: none; appearance: none; cursor: pointer; }
.aj-br-btn:hover:not(:disabled) { background: #0d3a42 !important; }
.aj-br-btn:disabled, .aj-br-ghost:disabled { opacity: .45; cursor: default; }
.aj-br-ghost:hover:not(:disabled) { background: var(--aj-bg-subtle) !important; }
.aj-br-disclose:hover { color: var(--aj-text) !important; }
@keyframes ajBrSpin { to { transform: rotate(360deg) } }
.aj-br-spin { animation: ajBrSpin .9s linear infinite; }
`

const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 730, background: 'rgba(4,21,24,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal: { width: '100%', maxWidth: 980, height: 'calc(100vh - 48px)', background: 'var(--aj-bg-page)', borderRadius: 12, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
  bar: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#07272D', padding: '10px 16px' },
  title: { fontFamily: '"Montserrat", sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#fff' },
  close: { background: 'transparent', border: 'none', color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer' },
  scroll: { flex: '1 1 auto', overflowY: 'auto', minHeight: 0, padding: '28px 32px 40px' },

  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '120px 20px', textAlign: 'center' },
  spinner: { width: 22, height: 22, borderRadius: '50%', border: '2px solid var(--aj-border)', borderTopColor: '#07272D', marginBottom: 16 },
  loadTitle: { fontFamily: SANS, fontSize: 15, fontWeight: 600, color: 'var(--aj-text)' },
  loadSub: { fontFamily: SANS, fontSize: 13, color: 'var(--aj-text-muted)', marginTop: 6 },

  hero: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, paddingBottom: 24, borderBottom: '0.5px solid var(--aj-border)' },
  eyebrow: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 6 },
  project: { fontFamily: SANS, fontSize: 24, fontWeight: 600, color: 'var(--aj-text)', letterSpacing: '-0.01em' },
  period: { fontFamily: SANS, fontSize: 13, color: 'var(--aj-text-mid)', marginTop: 4 },
  heroRight: { textAlign: 'right', flexShrink: 0 },
  total: { fontFamily: MONO, fontSize: 34, color: 'var(--aj-text)', letterSpacing: '-0.02em', lineHeight: 1.1 },
  split: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4 },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 },
  btn: { fontFamily: SANS, fontSize: 13, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer' },
  btnGhost: { fontFamily: SANS, fontSize: 13, fontWeight: 500, color: 'var(--aj-text)', background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },

  done: { marginTop: 18, padding: '12px 14px', fontFamily: SANS, fontSize: 13, color: 'var(--aj-text)', background: 'rgba(46,125,91,0.10)', borderRadius: 8, lineHeight: 1.5 },
  busy: { marginTop: 14, fontFamily: SANS, fontSize: 13, color: 'var(--aj-text-mid)' },
  err: { marginTop: 14, padding: '12px 14px', fontFamily: SANS, fontSize: 13, color: '#b3261e', background: 'rgba(179,38,30,0.08)', borderRadius: 8 },

  section: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', margin: '28px 0 10px' },
  emptyCard: { fontFamily: SANS, fontSize: 13, color: 'var(--aj-text-muted)', padding: 18, background: 'var(--aj-bg-surface)', borderRadius: 10, border: '0.5px solid var(--aj-border)' },

  card: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 12, padding: '18px 20px 8px', marginBottom: 14 },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, cursor: 'pointer' },
  phaseName: { fontFamily: SANS, fontSize: 17, fontWeight: 600, color: 'var(--aj-text)' },
  phaseMeta: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 3 },
  cardAmt: { textAlign: 'right', flexShrink: 0 },
  amt: { fontFamily: MONO, fontSize: 20, color: 'var(--aj-text)' },
  amtSub: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 3 },

  feeBlock: { marginTop: 16 },
  barTrack: { position: 'relative', height: 6, borderRadius: 3, background: 'var(--aj-bg-muted)', overflow: 'hidden' },
  barBefore: { position: 'absolute', left: 0, top: 0, bottom: 0, background: 'var(--aj-text-muted)', opacity: 0.55 },
  barNow: { position: 'absolute', top: 0, bottom: 0 },
  feeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginTop: 10, flexWrap: 'wrap' },
  feeLbl: { display: 'flex', alignItems: 'center', gap: 6, fontFamily: SANS, fontSize: 13, color: 'var(--aj-text-mid)' },
  feeFacts: { display: 'flex', gap: 18, fontFamily: SANS, fontSize: 12, color: 'var(--aj-text-mid)', flexWrap: 'wrap' },
  dotBefore: { display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--aj-text-muted)', opacity: 0.55, marginRight: 6, verticalAlign: 'middle' },
  dotNow: { display: 'inline-block', width: 8, height: 8, borderRadius: 2, marginRight: 6, verticalAlign: 'middle' },
  overTxt: { color: '#b3261e', fontWeight: 600 },
  hint: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 8 },
  pctWrap: { display: 'inline-flex', alignItems: 'center' },
  pctIn: { width: 42, fontFamily: MONO, fontSize: 13, textAlign: 'right', color: 'var(--aj-text)', borderRadius: 6, padding: '3px 4px' },
  pctSign: { fontFamily: MONO, fontSize: 13, color: 'var(--aj-text)', marginLeft: 1 },

  subDisclose: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, marginBottom: 8, padding: 0, background: 'transparent', border: 'none', fontFamily: SANS, fontSize: 12, fontWeight: 500, color: 'var(--aj-text-mid)', cursor: 'pointer' },
  chev: { display: 'inline-block', fontSize: 15, lineHeight: 1, transition: 'transform .15s', color: 'var(--aj-text-muted)' },

  tableWrap: { marginTop: 6, marginBottom: 12 },
  tableTitle: { display: 'flex', alignItems: 'center', gap: 12, fontFamily: SANS, fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', padding: '8px 0' },
  tHead: { fontFamily: SANS, fontSize: 11, fontWeight: 500, color: 'var(--aj-text-muted)', padding: '6px 0', borderBottom: '0.5px solid var(--aj-border)' },
  tRow: { display: 'grid', gridTemplateColumns: '18px 56px minmax(150px,1fr) 56px 64px 76px 70px 84px 136px', gap: 10, alignItems: 'center', fontFamily: SANS, fontSize: 13, color: 'var(--aj-text)' },
  eRow: { display: 'grid', gridTemplateColumns: '18px 56px minmax(150px,1fr) 84px 136px', gap: 10, alignItems: 'center', fontFamily: SANS, fontSize: 13, color: 'var(--aj-text)' },
  tItem: { padding: '10px 0 6px', borderBottom: '0.5px solid var(--aj-border)' },
  dim: { opacity: 0.5 },
  r: { textAlign: 'right', justifySelf: 'end' },
  mono: { fontFamily: MONO, fontSize: 12, color: 'var(--aj-text-mid)' },
  who: { display: 'flex', flexDirection: 'column', minWidth: 0, fontWeight: 500 },
  act: { fontWeight: 400, fontSize: 12, color: 'var(--aj-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  numIn: { width: 56, fontFamily: MONO, fontSize: 12, textAlign: 'right', color: 'var(--aj-text)', borderRadius: 6, padding: '4px 6px' },
  memoRow: { display: 'flex', alignItems: 'flex-start', gap: 8, paddingLeft: 84, marginTop: 4 },
  memo: { flex: '1 1 auto', fontFamily: SANS, fontSize: 13, lineHeight: 1.45, color: 'var(--aj-text-mid)', borderRadius: 6, padding: '4px 8px', marginLeft: -8, resize: 'none', fieldSizing: 'content', minHeight: 24 },
  status: { width: '100%', fontFamily: SANS, fontSize: 12, fontWeight: 500, color: '#2e7d5b', background: 'rgba(46,125,91,0.10)', border: 'none', borderRadius: 20, padding: '5px 12px' },
  statusNext: { color: '#8a5a00', background: 'rgba(214,150,0,0.14)' },
  statusOff: { color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)' },
  badge: { flexShrink: 0, fontFamily: SANS, fontSize: 11, color: '#8a5a00', background: 'rgba(214,150,0,0.14)', borderRadius: 20, padding: '3px 9px', whiteSpace: 'nowrap' },
  bulk: { display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, fontSize: 12, color: 'var(--aj-text-mid)' },
  bulkBtn: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, padding: '3px 9px', cursor: 'pointer' },
  bulkIn: { width: 52, fontFamily: MONO, fontSize: 12, textAlign: 'right', color: 'var(--aj-text)', borderRadius: 6, padding: '3px 6px' },

  disclose: { display: 'flex', alignItems: 'center', gap: 8, margin: '24px 0 10px', padding: 0, background: 'transparent', border: 'none', fontFamily: SANS, fontSize: 14, fontWeight: 600, color: 'var(--aj-text-mid)', cursor: 'pointer' },
  discloseSub: { fontWeight: 400, color: 'var(--aj-text-muted)', fontSize: 13 },
  quietCard: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 12, padding: '6px 20px' },
  qHead: { fontFamily: SANS, fontSize: 11, fontWeight: 500, color: 'var(--aj-text-muted)' },
  qRow: { display: 'grid', gridTemplateColumns: 'minmax(200px,1fr) 90px 80px 110px 110px', gap: 12, alignItems: 'center', padding: '9px 0', borderBottom: '0.5px solid var(--aj-border)', fontFamily: SANS, fontSize: 13, color: 'var(--aj-text)' },
  qName: { fontWeight: 500 },
  qCode: { fontWeight: 400, fontSize: 12, color: 'var(--aj-text-muted)', marginLeft: 6 },
  qType: { fontSize: 12, color: 'var(--aj-text-muted)' },
  qDash: { color: 'var(--aj-text-muted)' },
}
